// A build you can see at home (P68). While a style part is being built (the
// server's live.build: the pile delivered, the stage, how far along), the yard
// has a building site against the front of the house:
//  - scaffolding (poles and boards) from the moment the build is queued,
//  - the new part rising in stages: the frame (posts and a beam going up), then
//    the boards (one by one), then the paint (raw wood warming to its colour),
//  - a materials pile beside it that grows as trips come home: a timber stack,
//    a stone heap, clay pots, baskets of flowers, crates of the rest,
//  - and when it's done, a little ta-da, and the scaffold comes down.
// The plazmi carries each trip's bundle from the gate to the pile, and hammers
// at the part (P36's carry-log, drop-log, build-hammer, drop-plank); neighbours
// who help hammer too (onChore, through Life.extra). It all comes from server
// state and the clock, so every viewer sees the same.
import * as THREE from 'three';
import { mat, cyl, rbox, sphere, finishShadows } from './kit.js';
import { frontDoor } from './furnish.js';
import type { Life } from './life.js';
import './actions/places.js'; // carry-log, drop-log, build-hammer, drop-plank

type P = [number, number];
export type BuildLive = {
  part: string; name: string; phase: string; stage: 'pile' | 'frame' | 'boards' | 'paint' | 'done';
  progress: number; start?: number; ends?: number; pile?: Record<string, number>; carrying?: Record<string, number>;
  needs?: Record<string, number>; trips_done?: number; trips_left?: number; helpers?: string[]; words?: string; done_at?: number;
};
type Fx = { sparkles(at: THREE.Vector3, color?: string, n?: number, spread?: number): void; emoji(at: THREE.Vector3, text: string, size?: number, life?: number): void };

const WOOD = '#c9a46a', RAW = new THREE.Color('#d8b98a');
// what each material is carried as, and set down as (actions/places.ts)
const CARRY: Record<string, string> = { timber: 'log', stick: 'sticks', sticks: 'sticks', stone: 'stone', clay: 'clay', sand: 'pail' };

export class Construction {
  root = new THREE.Group();
  private site: P; private pileAt: P; private face: P;
  private scaffold = new THREE.Group();
  private part = new THREE.Group();
  private pile = new THREE.Group();
  private posts: THREE.Object3D[] = [];
  private beam: THREE.Object3D;
  private boards: THREE.Mesh[] = [];
  private paint = new THREE.MeshStandardMaterial({ color: RAW.clone(), roughness: 0.8 });
  private colour = new THREE.Color('#e9d8b4');
  private live: BuildLive | null = null;
  private pileKey = '';
  private tadaSeen = 0;
  private offset = 0;
  fx: Fx | null = null;
  changed = () => {};
  // the plazmi (and any helpers) cheer at the ta-da
  cheer: ((face: P) => void) | null = null;

  constructor(h: { plot: [number, number]; spec: any; resident?: { color?: string } }) {
    const [W, D] = h.plot;
    const door = frontDoor(h.spec) || [W / 2, D - 3];
    const clampX = (x: number) => Math.max(1.4, Math.min(W - 1.4, x));
    // against the front of the house, beside the door; the pile out on the lawn the other side
    const sx = clampX(door[0] + (door[0] + 3.2 < W - 1 ? 2.4 : -2.4)), sz = Math.min(D - 1.6, door[1] + 0.9);
    this.site = [sx, sz];
    this.pileAt = [clampX(door[0] + (sx > door[0] ? -2.8 : 2.8)), Math.min(D - 1.2, door[1] + 2.6)];
    this.face = [sx, sz - 2];
    if (h.resident?.color) this.colour.set(h.resident.color).lerp(new THREE.Color('#f4ead8'), 0.45);
    this.root.add(this.scaffold, this.part, this.pile);
    this.root.visible = false;
    // the scaffold: four poles, two board decks, a brace
    const pole = mat('#9aa3ad', { rough: 0.5, metal: 0.3 }), deck = mat(WOOD, { rough: 0.9 });
    for (const [dx, dz] of [[-1.3, -0.15], [1.3, -0.15], [-1.3, 0.75], [1.3, 0.75]]) {
      const p = cyl(0.05, 0.05, 3.1, pole, 6); p.position.set(sx + dx, 1.55, sz + dz); this.scaffold.add(p);
    }
    for (const y of [1.25, 2.45]) { const b = rbox(2.8, 0.06, 0.95, 0.01, deck); b.position.set(sx, y, sz + 0.3); this.scaffold.add(b); }
    const brace = cyl(0.03, 0.03, 3.0, pole, 5); brace.position.set(sx, 1.4, sz + 0.78); brace.rotation.z = Math.atan2(2.4, 2.6); this.scaffold.add(brace);
    // the new part: posts and a beam (the frame), then boards between them (the boards and the paint)
    const timber = mat('#b98a55', { rough: 0.85 });
    for (const dx of [-1.0, 0, 1.0]) { const p = rbox(0.14, 2.3, 0.14, 0.02, timber); p.position.set(sx + dx, 1.15, sz + 0.25); this.posts.push(p); this.part.add(p); }
    this.beam = rbox(2.3, 0.16, 0.18, 0.02, timber); this.beam.position.set(sx, 2.35, sz + 0.25); this.part.add(this.beam);
    for (let i = 0; i < 7; i++) {
      const b = new THREE.Mesh(rbox(2.1, 0.22, 0.05, 0.015, this.paint).geometry, this.paint);
      b.position.set(sx, 0.3 + i * 0.3, sz + 0.34); this.boards.push(b); this.part.add(b);
    }
    finishShadows(this.root);
  }

  // where its feet go for each job
  private spot(dx: number, dz: number): P { return [this.site[0] + dx, this.site[1] + dz]; }

  setLive(b: BuildLive | null | undefined, now: number) {
    this.offset = now - Date.now();
    const was = this.live;
    this.live = b || null;
    const on = !!b;
    if (on !== this.root.visible) { this.root.visible = on; this.changed(); }
    if (!b) return;
    // a finished build: the ta-da, once, and the scaffold comes down
    if (b.phase === 'done' && b.done_at && b.done_at !== this.tadaSeen) {
      this.tadaSeen = b.done_at;
      if (was && was.phase !== 'done' && this.fx) this.tada();
    }
    const key = JSON.stringify(b.pile || {}) + b.phase;
    if (key !== this.pileKey) { this.pileKey = key; this.buildPile(b.phase === 'done' || b.stage === 'paint' ? {} : b.pile || {}); this.changed(); }
  }

  private tada() {
    const at = new THREE.Vector3(this.site[0], 2.4, this.site[1] + 0.4).applyMatrix4(this.root.matrixWorld);
    const fx = this.fx!;
    // three bursts over a second, gold and the part's own colour, and a 🎉 over it
    const c = `#${this.colour.getHexString()}`;
    fx.sparkles(at, '#ffd36b', 30, 1.4);
    setTimeout(() => fx.sparkles(at.clone().add(new THREE.Vector3(-0.8, 0.5, 0)), c, 22, 1.0), 380);
    setTimeout(() => fx.sparkles(at.clone().add(new THREE.Vector3(0.8, 0.7, 0)), '#fff2b0', 22, 1.0), 760);
    fx.emoji(at.clone().add(new THREE.Vector3(0, 1.0, 0)), '🎉', 1.1, 3.6);
    this.cheer?.(this.site);
  }

  // the pile: a little of each thing delivered, growing with each trip home
  private buildPile(pile: Record<string, number>) {
    for (const c of [...this.pile.children]) this.pile.remove(c);
    const [px, pz] = this.pileAt;
    let col = 0;
    const put = (o: THREE.Object3D, x: number, y: number, z: number) => { o.position.set(px + x, y, pz + z); this.pile.add(o); };
    for (const [k, n0] of Object.entries(pile)) {
      const n = Math.min(12, n0), x0 = (col - 1) * 1.0;
      if (k === 'timber' || k === 'stick' || k === 'planks' || k === 'posts') {
        const m = mat(k === 'planks' ? '#d9b27a' : '#9a6b42', { rough: 0.9 });
        for (let i = 0; i < n; i++) { const row = Math.floor(i / 4), j = i % 4; const l = cyl(0.11, 0.11, 0.95, m, 8); l.rotation.z = Math.PI / 2; put(l, x0, 0.11 + row * 0.19, (j - 1.5) * 0.23 + (row % 2) * 0.1); }
      } else if (k === 'stone' || k === 'sand') {
        const m = mat(k === 'sand' ? '#e3cf9c' : '#a8a196', { rough: 1, flat: true });
        for (let i = 0; i < n; i++) { const s = sphere(0.16, m, 7, 5); s.scale.set(0.2, 0.15, 0.18); const a = i * 2.4; put(s, x0 + Math.cos(a) * 0.08 * (i % 4), 0.1 + Math.floor(i / 5) * 0.14, Math.sin(a) * 0.08 * (i % 4)); }
      } else if (k === 'clay') {
        const m = mat('#c0623f', { rough: 0.8 });
        for (let i = 0; i < Math.min(6, n); i++) { const p = cyl(0.12, 0.09, 0.26, m, 10); put(p, x0 + (i % 3 - 1) * 0.28, 0.13, Math.floor(i / 3) * 0.28 - 0.14); }
      } else if (k === 'flowers' || k === 'flax' || k === 'wool') {
        const wicker = mat('#c89a5a', { rough: 1 }), top = mat(k === 'flowers' ? '#e8a0b4' : k === 'flax' ? '#8fa8d8' : '#f4eee2', { rough: 1 });
        for (let i = 0; i < Math.min(3, Math.ceil(n / 3)); i++) { put(cyl(0.2, 0.16, 0.24, wicker, 10), x0, 0.12, (i - 1) * 0.42); const f = sphere(0.18, top, 8, 6); f.scale.set(0.19, 0.08, 0.19); put(f, x0, 0.26, (i - 1) * 0.42); }
      } else {
        const m = mat('#b98a55', { rough: 0.9 });
        for (let i = 0; i < Math.min(4, Math.ceil(n / 3)); i++) put(rbox(0.36, 0.28, 0.32, 0.02, m), x0, 0.14 + Math.floor(i / 2) * 0.28, ((i % 2) - 0.5) * 0.36);
      }
      col++;
    }
    finishShadows(this.pile);
  }

  // every frame: the part rises with the clock
  update() {
    const b = this.live;
    if (!b || !this.root.visible) return;
    const now = Date.now() + this.offset;
    const done = b.phase === 'done';
    const k = done ? 1 : b.start && b.ends && now >= b.start ? Math.min(1, (now - b.start) / Math.max(1, b.ends - b.start)) : 0;
    this.scaffold.visible = !done;
    this.part.visible = k > 0;
    // the frame: posts rise over the first third, the beam goes on at the end of it
    const f = Math.min(1, k * 3);
    for (const p of this.posts) { p.scale.y = Math.max(0.02, f); p.position.y = 1.15 * f; }
    this.beam.visible = f >= 1;
    // the boards: one by one over the second third
    const nb = Math.floor(Math.max(0, Math.min(1, (k - 1 / 3) * 3)) * this.boards.length + 0.001);
    this.boards.forEach((bd, i) => { bd.visible = i < nb; });
    // the paint: raw wood warming to its colour over the last third
    const pk = Math.max(0, Math.min(1, (k - 2 / 3) * 3));
    this.paint.color.copy(RAW).lerp(this.colour, pk);
    // the shadows follow the part as it rises (they're drawn on demand on the house page)
    const shape = `${Math.round(f * 10)}:${nb}:${done}`;
    if (shape !== this.shape) { this.shape = shape; this.changed(); }
  }
  private shape = '';

  // (Life.extra) a build's plan steps, played at the site: carrying a trip home, and building
  onChore = (life: Life, chore: string, ends: number) => {
    const b = this.live;
    if (chore === 'carry') {
      const doing = (life as any).live?.plan?.find((p: any) => p.ends === ends)?.doing || '';
      const m = Object.keys(CARRY).find((k) => doing.includes(k));
      const item = m ? CARRY[m] : null;
      const at: P = [this.pileAt[0], this.pileAt[1] + 0.9];
      if (item) life.planWork([{ action: `drop-${item}`, secs: 1.2, at, face: this.pileAt, carry: `carry-${item}` }], `carrying ${m} home to the pile`, true);
      else life.planWork([{ action: 'unpack', secs: 2.8, at, face: this.pileAt, carry: 'bundle' }], 'carrying a bundle home to the pile', true);
      return;
    }
    if (chore === 'build') {
      const label = b ? `building the ${b.name}` : 'building';
      const secs = Math.max(8, (ends - (Date.now() + this.offset)) / 1000 - 2);
      const n = Math.min(14, Math.ceil(secs / 7));
      const seq: { action: string; secs: number; at?: P; face?: P; carry?: string | null }[] = [];
      const side = Math.random() < 0.5 ? -1 : 1;
      const front = this.spot(side * 0.7, 1.6);
      for (let i = 0; i < n; i++) {
        if (i % 3 === 2) seq.push({ action: 'drop-plank', secs: 1.2, at: this.spot(side * 1.6, 1.9), face: this.face, carry: 'carry-plank' });
        seq.push({ action: 'build-hammer', secs: 4.4, at: i % 3 === 0 || i % 3 === 2 ? front : undefined, face: this.face });
      }
      // first a plank up from the pile
      seq.unshift({ action: 'drop-plank', secs: 1.2, at: front, face: this.face, carry: 'carry-plank' });
      life.planWork(seq, label);
    }
  };
}
